import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Observable, from, map, switchMap, take } from 'rxjs';
import { Clinic } from '../models/clinic.model';
import { Specialist } from '../models/specialist.model';
import { User } from '../models/user.model';
import { SpecialistService } from './specialist.service';
import { UserService } from './user.service';
import { convertSnaps } from './utils.service';

@Injectable({
  providedIn: 'root',
})
export class ClinicService {
  constructor(
    private readonly db: AngularFireDatabase,
    private readonly specialistService: SpecialistService,
    private readonly userService: UserService,
  ) {}

  findClinics(): Observable<Clinic[]> {
    return this.db
      .list(`${localStorage.getItem('appId')}_clinics`)
      .snapshotChanges()
      .pipe(map((results) => convertSnaps<Clinic>(results)));
  }

  findBySlug(clinicSlug: string): Observable<Clinic | null> {
    return this.db
      .list(`${localStorage.getItem('appId')}_clinics`, (ref) =>
        ref.orderByChild('slug').equalTo(clinicSlug),
      )
      .snapshotChanges()
      .pipe(
        map((results) => {
          const clinics = convertSnaps<Clinic>(results);
          return clinics.length === 1 ? clinics[0] : null;
        }),
      );
  }

  findSpecialists(clinicId: string): Observable<Specialist[]> {
    return this.db
      .list(`${localStorage.getItem('appId')}_clinics/${clinicId}/specialists`)
      .snapshotChanges()
      .pipe(
        map((results) => {
          const ids = results.map((snap: any) => snap.payload.val());
          return ids;
        }),
        switchMap((ids: string[]) => this.specialistService.findSpecialistsByKeys(ids)),
      );
  }

  findUsers(clinicId: string): Observable<User[]> {
    return this.userService.findUsersByClinicId(clinicId);
  }

  createClinic(clinic: Partial<Clinic>, clinicId?: string) {
    let save$: Observable<any>;

    if (clinicId) {
      save$ = from(
        this.db.object(`${localStorage.getItem('appId')}_clinics/${clinicId}`).set(clinic),
      );
    } else {
      save$ = from(this.db.list(`${localStorage.getItem('appId')}_clinics`).push(clinic));
    }

    return save$.pipe(
      map((res) => {
        const newAppId = clinicId ?? res.key;
        return {
          id: newAppId,
          ...clinic,
        };
      }),
    );
  }

  update(clinicId: string, changes: Partial<Clinic>) {
    return from(
      this.db.object(`${localStorage.getItem('appId')}_clinics/${clinicId}`).update(changes),
    );
  }

  addSpecialists(clinicId: string, specialistIds: string[]): Observable<void> {
    const clinicSpecialistsPath = `${localStorage.getItem(
      'appId',
    )}_clinics/${clinicId}/specialists`;

    return this.db
      .object<string[]>(clinicSpecialistsPath)
      .valueChanges()
      .pipe(
        take(1), // Take the current value and complete
        switchMap((existingSpecialists) => {
          const updatedSpecialists = existingSpecialists ? [...existingSpecialists] : [];

          specialistIds.forEach((id) => {
            if (!updatedSpecialists.includes(id)) {
              updatedSpecialists.push(id);
            }
          });

          return from(this.db.object(clinicSpecialistsPath).set(updatedSpecialists));
        }),
      );
  }

  removeSpecialist(clinicId: string, specialistId: string): Observable<void> {
    const clinicSpecialistsRef = this.db.object<string[]>(
      `${localStorage.getItem('appId')}_clinics/${clinicId}/specialists`,
    );

    return clinicSpecialistsRef.snapshotChanges().pipe(
      take(1),
      switchMap((snapshot) => {
        const specialists = snapshot.payload.val() || [];
        const updatedSpecialists = specialists.filter((id) => id !== specialistId);
        return from(clinicSpecialistsRef.set(updatedSpecialists));
      }),
    );
  }

  async deleteClinic(clinic: Clinic) {
    const appId = localStorage.getItem('appId');

    const usersRef = this.db.database.ref(`${appId}_users`);
    const visitsRef = this.db.database.ref(`${appId}_visits`);
    const petsRef = this.db.database.ref(`${appId}_pets`);
    const petDeletionPromises = [];

    const userSnapshot = await usersRef.orderByChild('clinicId').equalTo(clinic.id).once('value');

    const userDeletionPromises = [];
    const visitDeletionPromises = [];

    userSnapshot.forEach((userChildSnapshot) => {
      const userId = userChildSnapshot.key;

      visitDeletionPromises.push(
        visitsRef
          .orderByChild('patientId')
          .equalTo(userId)
          .once('value')
          .then((visitSnapshot) => {
            const individualVisitDeletionPromises = [];
            visitSnapshot.forEach((visitChildSnapshot) => {
              individualVisitDeletionPromises.push(visitChildSnapshot.ref.remove());
            });
            return Promise.all(individualVisitDeletionPromises);
          }),
      );

      petDeletionPromises.push(
        petsRef
          .orderByChild('ownerId')
          .equalTo(userId)
          .once('value')
          .then((petSnapshot) => {
            const individualPetDeletionPromises = [];
            petSnapshot.forEach((petChildSnapshot) => {
              individualPetDeletionPromises.push(petChildSnapshot.ref.remove());
            });
            return Promise.all(individualPetDeletionPromises);
          }),
      );

      userDeletionPromises.push(userChildSnapshot.ref.remove());
    });

    await Promise.all(visitDeletionPromises);
    await Promise.all(petDeletionPromises);
    await Promise.all(userDeletionPromises);
    await this.db.database.ref(`${appId}_clinics/${clinic.id}`).remove();
  }
}
